import { fn, get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import SettingsCostUnitToggle from 'lumen/components/settings/cost/unit-toggle';
import type {
  BookingWindowRow,
  CommissionUnit,
} from 'lumen/utils/net-revenue/cost/types';
import {
  maxForUnit,
  placeholderForUnit,
  stepForUnit,
} from 'lumen/utils/net-revenue/cost/units';
import { getWeekDayColumns } from 'lumen/utils/pricing-assistant/recommendation-settings';

import styles from './advanced-matrix.module.css';

interface WeekdayColumn {
  id: number;
  label: string;
  valueIndex: number;
}

interface SettingsCostAdvancedMatrixSignature {
  Args: {
    bookingWindows: BookingWindowRow[];
    costError?: string;
    onCellChange: (
      bookingWindowId: string,
      dayIndex: number,
      value: number | null,
    ) => void;
    onUnitChange: (unit: CommissionUnit) => void;
    unit: CommissionUnit;
  };
  Element: HTMLDivElement;
}

/** Only the "All (default)" booking window renders today. */
export default class SettingsCostAdvancedMatrix extends Component<SettingsCostAdvancedMatrixSignature> {
  @service declare intl: Services['intl'];

  get amountStep(): 0.01 | 1 {
    return stepForUnit(this.args.unit);
  }

  get columns(): WeekdayColumn[] {
    // ISO weekday is Mon=1 … Sun=7, but the matrix indexes 0=Mon … 6=Sun.
    return getWeekDayColumns().map((column) => ({
      id: column.id,
      label: this.intl.t(`days_short.${column.isoWeekDay}`),
      valueIndex: column.id - 1,
    }));
  }

  get maxAmount(): number | undefined {
    return maxForUnit(this.args.unit);
  }

  get placeholder(): string {
    return placeholderForUnit(this.args.unit);
  }

  <template>
    <div
      class="rounded-200 bg-neutral-subdued flex flex-col gap-300 p-400"
      data-test-id="cost-advanced-matrix"
      ...attributes
    >
      <SettingsCostUnitToggle @onChange={{@onUnitChange}} @unit={{@unit}} />

      <div class={{styles.matrix}}>
        <span class="text-neutral-subdued text-200--bold text-left">
          {{t "settings.cost.advanced.booking_window"}}
        </span>
        {{#each this.columns key="id" as |column|}}
          <span class="text-neutral-subdued text-200--bold text-left">
            {{column.label}}
          </span>
        {{/each}}

        {{#each @bookingWindows key="id" as |bookingWindow|}}
          <span class="text-300--regular">{{bookingWindow.label}}</span>
          {{#each this.columns key="id" as |column|}}
            {{#if (eq @unit "percentage")}}
              <PrismNumberInput
                @max={{this.maxAmount}}
                @min={{0}}
                @onValueChange={{fn
                  @onCellChange
                  bookingWindow.id
                  column.valueIndex
                }}
                @placeholder={{this.placeholder}}
                @removePadding={{true}}
                @step={{this.amountStep}}
                @value={{get bookingWindow.values column.valueIndex}}
                aria-label={{t
                  "settings.cost.advanced.cell_label"
                  day=column.label
                  window=bookingWindow.label
                }}
                class="w-full"
                data-test-id="cost-advanced-cell-{{bookingWindow.id}}-{{column.id}}"
              />
            {{else}}
              <PrismNumberInput
                @max={{this.maxAmount}}
                @min={{0}}
                @onValueChange={{fn
                  @onCellChange
                  bookingWindow.id
                  column.valueIndex
                }}
                @placeholder={{this.placeholder}}
                @removePadding={{true}}
                @step={{this.amountStep}}
                @value={{get bookingWindow.values column.valueIndex}}
                aria-label={{t
                  "settings.cost.advanced.cell_label"
                  day=column.label
                  window=bookingWindow.label
                }}
                class="w-full"
                data-test-id="cost-advanced-cell-{{bookingWindow.id}}-{{column.id}}"
              />
            {{/if}}
          {{/each}}
        {{/each}}
      </div>

      {{#if @costError}}
        <p
          class="text-critical-default text-200--regular"
          data-test-id="cost-advanced-error"
          role="alert"
        >
          {{@costError}}
        </p>
      {{/if}}
    </div>
  </template>
}
